import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { pedir } from '../api/cliente';
import { Boton, Tarjeta } from './ui/basicos';
import { Campo, Selector } from './ui/formulario';
import { useTextoDeError } from './ui/estados';
import { useFiscalDeLaEmpresa } from '../sesion/fiscal';

/** Lo que hace falta saber del tercero para poder corregirlo. Es un subconjunto a propósito. */
export interface TerceroCorregible {
  readonly id: string;
  readonly razonSocial: string;
  readonly nombreComercial: string | null;
  readonly tipoDeIdentificacion: string;
  readonly identificacion: string | null;
  readonly condicionIva: string;
  readonly telefono: string | null;
  readonly correo: string | null;
}

/**
 * Los datos del tercero, en la ficha.
 *
 * ## Por qué es un componente y no está escrito en cada ficha
 *
 * Porque clientes y proveedores son la misma entidad mirada desde dos lados —lo dice el modelo
 * desde E5: un tercero con perfil de cliente, de proveedor, o los dos— así que corregir su razón
 * social es una sola cosa. **El dueño del producto pidió dos pantallas separadas**, y ésta es la
 * forma de dárselas sin que dentro de seis meses una tenga un campo que la otra no: lo que se
 * duplica es la ruta, no el formulario.
 *
 * ## Lo que no está acá
 *
 * Los perfiles. El límite de crédito y los días de pago tienen sus propias rutas y **sus propios
 * permisos** desde E5: quien corrige un teléfono no necesariamente puede tocar cuánto se le fía a
 * alguien. Cada ficha monta el suyo.
 */
export function DatosDelTercero({
  tercero,
  puedeEditar,
  alGuardar,
}: {
  readonly tercero: TerceroCorregible;
  readonly puedeEditar: boolean;
  readonly alGuardar: readonly string[];
}): React.ReactElement {
  const { t } = useTranslation();
  const fiscal = useFiscalDeLaEmpresa();
  const cola = useQueryClient();
  const textoDeError = useTextoDeError();
  const [error, setError] = useState<string | null>(null);
  const [edicion, setEdicion] = useState<Record<string, string>>({});

  // Lo escrito gana sobre lo que había; sin tocar nada, `edicion` está vacío y no se manda nada.
  const valor = (campo: keyof TerceroCorregible): string =>
    edicion[campo] ?? String(tercero[campo] ?? '');

  const guardar = useMutation({
    mutationFn: () => {
      // Sólo lo que cambió, como toda corrección desde E15.
      const cambios: Record<string, string> = {};
      for (const [campo, escrito] of Object.entries(edicion)) {
        const original = String(tercero[campo as keyof TerceroCorregible] ?? '');
        if (escrito.trim() !== original) cambios[campo] = escrito.trim();
      }
      return pedir<{ id: string }>(`/terceros/${tercero.id}`, {
        method: 'PATCH',
        cuerpo: cambios,
      });
    },
    onSuccess: async () => {
      setEdicion({});
      setError(null);
      for (const clave of alGuardar) await cola.invalidateQueries({ queryKey: [clave] });
    },
    onError: (fallo: unknown) => {
      setError(textoDeError(fallo));
    },
  });

  return (
    <Tarjeta
      titulo={t('ficha.datos')}
      descripcion={t('terceros.corregirAyuda')}
      acciones={
        puedeEditar ? (
          <Boton
            tono="principal"
            cargando={guardar.isPending}
            onClick={() => {
              guardar.mutate();
            }}
          >
            {t('acciones.guardar')}
          </Boton>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <Campo
            etiqueta={t('clientes.razonSocial')}
            value={valor('razonSocial')}
            disabled={!puedeEditar}
            onChange={(evento) => {
              setEdicion({ ...edicion, razonSocial: evento.target.value });
            }}
          />
          <Campo
            etiqueta={t('clientes.nombreComercial')}
            value={valor('nombreComercial')}
            disabled={!puedeEditar}
            onChange={(evento) => {
              setEdicion({ ...edicion, nombreComercial: evento.target.value });
            }}
          />
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <Selector
            etiqueta={t('clientes.tipoDeIdentificacion')}
            value={valor('tipoDeIdentificacion')}
            disabled={!puedeEditar}
            onChange={(evento) => {
              setEdicion({ ...edicion, tipoDeIdentificacion: evento.target.value });
            }}
          >
            {fiscal.tiposDeTercero.map((tipo) => (
              <option key={tipo} value={tipo}>
                {fiscal.nombreDeTipo(tipo)}
              </option>
            ))}
          </Selector>
          {/* Corregir el número **se revalida en el servidor** con el paquete de país: esto no es
              la defensa, es la comodidad de escribirlo donde se lo está mirando. */}
          <Campo
            etiqueta={t('clientes.identificacion')}
            value={valor('identificacion')}
            disabled={!puedeEditar}
            onChange={(evento) => {
              setEdicion({ ...edicion, identificacion: evento.target.value });
            }}
          />
          <Selector
            etiqueta={t('clientes.condicionIva')}
            value={valor('condicionIva')}
            disabled={!puedeEditar}
            onChange={(evento) => {
              setEdicion({ ...edicion, condicionIva: evento.target.value });
            }}
          >
            {fiscal.condicionesDeTercero.map((condicion) => (
              <option key={condicion} value={condicion}>
                {fiscal.nombreDeCondicion(condicion)}
              </option>
            ))}
          </Selector>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Campo
            etiqueta={t('clientes.telefono')}
            value={valor('telefono')}
            disabled={!puedeEditar}
            onChange={(evento) => {
              setEdicion({ ...edicion, telefono: evento.target.value });
            }}
          />
          <Campo
            etiqueta={t('clientes.correo')}
            value={valor('correo')}
            disabled={!puedeEditar}
            onChange={(evento) => {
              setEdicion({ ...edicion, correo: evento.target.value });
            }}
          />
        </div>
        {error === null ? null : (
          <p role="alert" className="text-xs text-mal">
            {error}
          </p>
        )}
      </div>
    </Tarjeta>
  );
}
